import { useCallback, useMemo, type ReactNode, type RefCallback, type RefObject } from 'react';

import type { NavItemConfigWithCustomizationOptions } from '../../types';
import {
	createNestedItemData,
	getNestedItemData,
	type IsItemDraggable,
	isNestedItemData,
	isNestedItemDataInGroup,
	type RawItemData,
	type TNestedItemData,
} from '../dnd-data';
import type { AnalyticsEventHandler } from '../use-item-drop';

import { useItemDragAndDrop } from './index';

export interface NestedItemDragAndDrop {
	draggableRef: RefCallback<HTMLAnchorElement | HTMLButtonElement> | undefined;
	dropIndicator: ReactNode | undefined;
	dropTargetRef: RefObject<HTMLDivElement> | undefined;
	isDragAndDropEnabled?: boolean;
	isDragging: boolean;
	isItemDraggable: boolean;
	maybeDragPreview: ReactNode;
}

interface UseNestedItemDragAndDrop {
	data: RawItemData;
	entityAri: string;
	fireAnalyticsEvent?: AnalyticsEventHandler;
	group: string;
	id: string;
	isDragAndDropEnabled?: boolean;
	isExpandable: boolean;
	isExpandOrFlyoutOpen: boolean;
	item?: NavItemConfigWithCustomizationOptions;
	onExpandFlyoutToggle: (open: boolean) => void;
	ownerAri: string;
}

export function useNestedItemDragAndDrop({
	data,
	group,
	entityAri,
	fireAnalyticsEvent,
	id,
	isDragAndDropEnabled,
	isExpandable,
	isExpandOrFlyoutOpen,
	item,
	onExpandFlyoutToggle,
	ownerAri,
}: UseNestedItemDragAndDrop): NestedItemDragAndDrop {
	const nestedItemData = useMemo(
		() =>
			createNestedItemData({
				entityAri,
				group,
				isReorderable: true,
				label: data.label,
				menuItemId: id,
				ownerAri,
			}),
		[data.label, entityAri, group, id, ownerAri],
	);

	const isItemDraggable: IsItemDraggable<TNestedItemData> = useCallback(() => {
		return isDragAndDropEnabled !== false && nestedItemData.entityAri === entityAri;
	}, [entityAri, isDragAndDropEnabled, nestedItemData.entityAri]);

	return useItemDragAndDrop<TNestedItemData>({
		data: nestedItemData,
		fireAnalyticsEvent,
		item,
		id,
		isExpandable,
		isExpandOrFlyoutOpen,
		onExpandFlyoutToggle,
		isDragAndDropEnabled,
		isItemDraggable,
		group,
		getItemData: getNestedItemData,
		isItemData: isNestedItemData,
		isItemDataInGroup: isNestedItemDataInGroup,
	});
}
